<template>
  <div class="vendas-pdv-page">

    <!-- ── Cabeçalho ─────────────────────────────────────────────────────── -->
    <div class="page-header">
      <div>
        <h1 class="page-header__title">Vendas do PDV</h1>
        <p class="page-header__sub">
          Consulte, imprima, transmita e acompanhe as vendas emitidas neste terminal.
        </p>
      </div>

      <div class="header-actions">
        <button
          class="vp-btn vp-btn--primary"
          @click="$router.push({ name: 'pdvDesktopNovaVenda' })"
        >
          <i class="fas fa-plus mr-1"></i> Nova venda
        </button>
        <button
          class="vp-btn vp-btn--outline"
          :disabled="sincronizandoPendentes || !online"
          :title="!online ? 'Disponível somente com internet' : ''"
          @click="sincronizarPendentes"
        >
          <span v-if="sincronizandoPendentes">
            <span class="spinner-border spinner-border-sm mr-1"></span>Sincronizando...
          </span>
          <span v-else>
            <i class="fas fa-paper-plane mr-1"></i>Sincronizar pendentes
          </span>
        </button>
        <button class="vp-btn vp-btn--outline" :disabled="carregando" @click="recarregar">
          <i class="fas fa-sync-alt mr-1"></i>Atualizar
        </button>
        <button
          class="vp-btn vp-btn--outline"
          :disabled="exportandoXml"
          @click="abrirBackupXml"
        >
          <i class="fas fa-file-export mr-1"></i>Backup XML
        </button>
        <button
          class="vp-btn vp-btn--ghost"
          @click="$router.push({ name: 'pdvDesktopDashboard' })"
        >
          <i class="fas fa-arrow-left mr-1"></i>Voltar ao PDV
        </button>
      </div>
    </div>

    <!-- ── Alertas ───────────────────────────────────────────────────────── -->
    <div v-if="erro"    class="alert alert-danger">{{ erro }}</div>
    <div v-if="sucesso" class="alert alert-success">✓ {{ sucesso }}</div>

    <!-- ── Filtros ───────────────────────────────────────────────────────── -->
    <div class="filters-card">
      <form class="filters-grid" @submit.prevent="aplicarFiltros">
        <div class="filter-field">
          <label>Cliente</label>
          <input v-model="filtros.cliente" type="text" placeholder="Nome do cliente" />
        </div>

        <div class="filter-field">
          <label>Número</label>
          <input v-model="filtros.numero" type="text" placeholder="Nº venda/NFC-e" />
        </div>

        <div class="filter-field">
          <label>De</label>
          <input v-model="filtros.dataInicio" type="date" />
        </div>

        <div class="filter-field">
          <label>Até</label>
          <input v-model="filtros.dataFim" type="date" />
        </div>

        <div class="filter-field">
          <label>Status</label>
          <select v-model="filtros.status">
            <option value="">Todos</option>
            <option value="pre_venda">Pré-venda</option>
            <option value="autorizada">Autorizada</option>
            <option value="pendente_transmissao_backend">Pendente transmissão</option>
            <option value="xml_assinado">XML assinado</option>
            <option value="pendente_xml">Pendente XML</option>
            <option value="rejeitada">Rejeitada</option>
            <option value="erro_transmissao">Erro transmissão</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>

        <div class="filter-field">
          <label>Tipo emissão</label>
          <select v-model="filtros.tipoEmissao">
            <option value="">Todos</option>
            <option value="1">Normal</option>
            <option value="9">Contingência</option>
          </select>
        </div>

        <div class="filter-actions">
          <button type="submit" class="vp-btn vp-btn--primary vp-btn--sm">
            <i class="fas fa-search mr-1"></i>Filtrar
          </button>
          <button type="button" class="vp-btn vp-btn--ghost vp-btn--sm" @click="limparFiltros">
            Limpar
          </button>
        </div>
      </form>
    </div>

    <!-- ── Tabela ────────────────────────────────────────────────────────── -->
    <div class="sales-table-card">
      <div v-if="carregando" class="vp-loading">
        <span class="spinner-border spinner-border-sm mr-2"></span>Carregando vendas...
      </div>

      <div v-else class="sales-table-wrapper">
        <table class="vp-table">
        <thead>
          <tr>
            <th>Número</th>
            <th>Série</th>
            <th>Emissão</th>
            <th>Cliente</th>
            <th class="text-right">Valor total</th>
            <th>Status</th>
            <th>Tipo</th>
            <th class="text-center">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="vendas.length === 0">
            <td colspan="8" class="text-center text-muted py-4">
              Nenhuma venda encontrada com os filtros atuais.
            </td>
          </tr>

          <tr v-for="nfce in vendas" :key="nfce.uuid_venda_terminal">
            <td class="vp-numero">{{ numeroVendaExibicao(nfce) }}</td>
            <td>{{ nfce.serie || '—' }}</td>
            <td>{{ (nfce.emissao || nfce.dtVenda || nfce.created_at) | dataHora }}</td>
            <td>{{ nfce.nomeCliente || 'Consumidor final' }}</td>
            <td class="text-right font-monospace">{{ nfce.valorTotal | brl }}</td>
            <td>
              <span :class="['vp-badge', badgeClassStatusVenda(nfce)]">
                {{ labelStatusVenda(nfce) }}
              </span>
            </td>
            <td>
              <span
                v-if="statusEfetivo(nfce) === 'pre_venda'"
                class="vp-badge vp-badge--info"
                title="Venda concluída com XML preparado, ainda não transmitido à SEFAZ"
              >Pré-venda</span>
              <span
                v-else-if="getTpEmis(nfce) === 9"
                class="vp-badge vp-badge--warning"
                title="NFC-e emitida em contingência offline"
              >Contingência</span>
              <span v-else class="vp-badge vp-badge--neutral">Normal</span>
            </td>
            <td class="text-center vp-table__actions">
              <button
                class="vp-btn vp-btn--outline vp-btn--sm"
                @click.stop="toggleMenu(nfce.uuid_venda_terminal)"
              >
                Ações <i class="fas fa-caret-down ml-1"></i>
              </button>

              <div
                v-if="menuAberto === nfce.uuid_venda_terminal"
                v-click-outside="closeMenu"
                class="vp-dropdown"
              >
                <button
                  v-for="acao in acoesDisponiveis(nfce)"
                  :key="acao.key"
                  class="vp-dropdown__item"
                  :class="acao.classe || ''"
                  :disabled="acao.disabled === true"
                  :title="acao.title || ''"
                  @click="executarAcao(acao, nfce)"
                >
                  <i v-if="acao.icone" :class="acao.icone" class="mr-2"></i>
                  {{ acao.label }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        </table>
      </div>

      <div v-if="!carregando && podeCarregarMais" class="vp-load-more">
        <button class="vp-btn vp-btn--outline" :disabled="carregandoMais" @click="carregarMais">
          <span v-if="carregandoMais">
            <span class="spinner-border spinner-border-sm mr-1"></span>Carregando...
          </span>
          <span v-else>Carregar mais</span>
        </button>
      </div>
    </div>

    <!-- ── Modais ─────────────────────────────────────────────────────────── -->
    <CorrigirFiscalModal
      v-if="modalCorrigir.visivel && modalCorrigir.nfce"
      :nfce="modalCorrigir.nfce"
      @fechar="modalCorrigir.visivel = false"
      @atualizar="_onCorrecaoAtualizada"
    />

    <DanfeNfce
      :visivel="modalDanfe.visivel"
      :uuid="modalDanfe.uuid"
      @fechar="modalDanfe.visivel = false"
    />

    <NfceDetalheModal
      v-if="modalDetalhe.visivel && modalDetalhe.nfce"
      :nfce="modalDetalhe.nfce"
      @fechar="modalDetalhe.visivel = false"
    />

    <CancelarNfceModal
      v-if="modalCancelar.visivel && modalCancelar.nfce"
      :nfce="modalCancelar.nfce"
      @fechar="fecharModalCancelar"
      @cancelado="_onNfceCancelada"
    />

    <!-- Modal XML (autorizado ou assinado, conforme contexto) -->
    <div
      v-if="modalXml.visivel"
      class="vp-modal-overlay"
      @click.self="fecharModalXml"
    >
      <div class="vp-modal-xml">
        <div class="vp-modal-xml__header">
          <div>
            <h4 class="vp-modal-xml__title">
              {{ modalXml.titulo }}
            </h4>
            <small v-if="modalXml.nfce && modalXml.nfce.chave" class="text-muted">
              Chave: {{ modalXml.nfce.chave }}
            </small>
          </div>
          <button class="vp-btn vp-btn--ghost" @click="fecharModalXml">✕</button>
        </div>

        <pre v-if="modalXml.conteudo" class="vp-modal-xml__content">{{ modalXml.conteudo }}</pre>
        <div v-else class="vp-modal-xml__empty">
          {{ modalXml.mensagemVazio }}
        </div>

        <div class="vp-modal-xml__footer">
          <span v-if="xmlCopiado" class="text-success mr-3 font-weight-bold">
            <i class="fas fa-check mr-1"></i>{{ mensagemToast || 'Copiado!' }}
          </span>
          <button
            class="vp-btn vp-btn--outline vp-btn--sm"
            :disabled="!modalXml.conteudo"
            @click="copiarXmlAtivo"
          >
            <i class="fas fa-copy mr-1"></i>Copiar XML
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Backup / Exportar XMLs -->
    <div
      v-if="modalBackup.visivel"
      class="vp-modal-overlay"
      @click.self="fecharBackupXml"
    >
      <div class="vp-modal-backup">
        <div class="vp-modal-backup__header">
          <h4 class="vp-modal-backup__title">
            <i class="fas fa-file-export mr-2"></i>Backup de XMLs
          </h4>
          <button class="vp-btn vp-btn--ghost" :disabled="exportandoXml" @click="fecharBackupXml">✕</button>
        </div>

        <div class="vp-modal-backup__body">
          <p class="text-muted">
            Selecione o período das NFC-e e a pasta onde os XMLs serão salvos.
          </p>

          <div class="vp-backup-grid">
            <div class="filter-field">
              <label>Data inicial</label>
              <input v-model="modalBackup.dataInicio" type="date" :disabled="exportandoXml" />
            </div>
            <div class="filter-field">
              <label>Data final</label>
              <input v-model="modalBackup.dataFim" type="date" :disabled="exportandoXml" />
            </div>
          </div>

          <div class="filter-field">
            <label>Pasta de destino</label>
            <div class="vp-backup-pasta">
              <input
                :value="modalBackup.pasta || 'Nenhuma pasta selecionada'"
                type="text"
                readonly
              />
              <button
                class="vp-btn vp-btn--outline vp-btn--sm"
                :disabled="exportandoXml"
                @click="selecionarPastaBackup"
              >
                <i class="fas fa-folder-open mr-1"></i>Escolher
              </button>
            </div>
          </div>

          <div v-if="backupErro" class="alert alert-danger mt-2">{{ backupErro }}</div>

          <div v-if="modalBackup.resumo" class="vp-backup-resumo">
            <h5>Resumo do backup</h5>
            <ul>
              <li>XMLs encontrados: <strong>{{ modalBackup.resumo.encontrados }}</strong></li>
              <li>Exportados: <strong>{{ modalBackup.resumo.exportados }}</strong></li>
              <li>Com erro: <strong>{{ modalBackup.resumo.erros }}</strong></li>
              <li>Não encontrados: <strong>{{ modalBackup.resumo.naoEncontrados }}</strong></li>
              <li class="vp-backup-resumo__path">Pasta: <code>{{ modalBackup.resumo.destino }}</code></li>
            </ul>
          </div>
        </div>

        <div class="vp-modal-backup__footer">
          <button class="vp-btn vp-btn--ghost" :disabled="exportandoXml" @click="fecharBackupXml">Fechar</button>
          <button
            class="vp-btn vp-btn--primary"
            :disabled="exportandoXml || !modalBackup.pasta"
            @click="exportarXmls"
          >
            <span v-if="exportandoXml">
              <span class="spinner-border spinner-border-sm mr-1"></span>Exportando...
            </span>
            <span v-else><i class="fas fa-download mr-1"></i>Exportar XMLs</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { getElectronAPI }    from "@/plugins/desktop";
import SyncPdvDesktopService from "../services/SyncPdvDesktopService";
import NfcePdvDesktopService from "../services/NfcePdvDesktopService";
import NfceLocalRepository   from "../repositories/NfceLocalRepository";
import CorrigirFiscalModal   from "../components/CorrigirFiscalModal.vue";
import DanfeNfce             from "../components/DanfeNfce.vue";
import NfceDetalheModal      from "../components/NfceDetalheModal.vue";
import CancelarNfceModal     from "../components/CancelarNfceModal.vue";

const PAGE_SIZE = 100;

const STATUS_LABEL = {
  pre_venda:                    "Pré-venda",
  autorizada:                   "Autorizada",
  pendente_transmissao_backend: "Pendente transmissão",
  pendente_transmissao:         "Pendente transmissão",
  rejeitada:                    "Rejeitada",
  erro_transmissao:             "Erro transmissão",
  erro:                         "Erro",
  cancelada:                    "Cancelada",
  xml_assinado:                 "XML assinado",
  xml_gerado:                   "XML gerado",
  pendente_xml:                 "Pendente XML",
};

const STATUS_BADGE = {
  pre_venda:                    "vp-badge--info",
  autorizada:                   "vp-badge--success",
  pendente_transmissao_backend: "vp-badge--warning",
  pendente_transmissao:         "vp-badge--warning",
  rejeitada:                    "vp-badge--warning",
  erro_transmissao:             "vp-badge--danger",
  erro:                         "vp-badge--danger",
  cancelada:                    "vp-badge--danger",
  xml_assinado:                 "vp-badge--info",
  xml_gerado:                   "vp-badge--info",
  pendente_xml:                 "vp-badge--neutral",
};

// Diretiva simples de click outside para fechar o dropdown
const clickOutside = {
  bind(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el.__clickOutsideHandler__);
  },
  unbind(el) {
    document.removeEventListener("click", el.__clickOutsideHandler__);
  },
};

export default {
  name: "VendasOffline",

  components: { CorrigirFiscalModal, DanfeNfce, NfceDetalheModal, CancelarNfceModal },

  directives: { "click-outside": clickOutside },

  filters: {
    dataHora(dt) {
      if (!dt) return "—";
      try {
        return new Date(dt).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      } catch { return dt; }
    },
    brl(v) {
      return Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
  },

  data() {
    return {
      vendas:           [],
      online:           typeof navigator !== "undefined" ? navigator.onLine : true,
      carregando:       false,
      carregandoMais:   false,
      podeCarregarMais: false,
      erro:             null,
      sucesso:          null,

      filtros: {
        cliente:     "",
        numero:      "",
        dataInicio:  "",
        dataFim:     "",
        status:      "",
        tipoEmissao: "",
      },

      paginacao: { limit: PAGE_SIZE, offset: 0 },

      sincronizandoPendentes: false,
      menuAberto:             null,

      modalCorrigir: { visivel: false, nfce: null },
      modalDanfe:    { visivel: false, uuid: null },
      modalDetalhe:  { visivel: false, nfce: null },
      modalCancelar: { visivel: false, nfce: null },

      modalXml: {
        visivel:        false,
        nfce:           null,
        tipo:           "autorizado", // 'autorizado' | 'assinado'
        titulo:         "",
        conteudo:       "",
        mensagemVazio:  "",
      },
      xmlCopiado:    false,
      mensagemToast: null,

      exportandoXml: false,
      backupErro:    null,
      modalBackup: {
        visivel:    false,
        dataInicio: "",
        dataFim:    "",
        pasta:      "",
        resumo:     null,
      },
    };
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }
    this._onOnline  = () => { this.online = true; };
    this._onOffline = () => { this.online = false; };
    window.addEventListener("online",  this._onOnline);
    window.addEventListener("offline", this._onOffline);
    await this.recarregar();
  },

  beforeDestroy() {
    window.removeEventListener("online",  this._onOnline);
    window.removeEventListener("offline", this._onOffline);
  },

  methods: {
    /**
     * Extrai tpEmis com prioridade:
     * 1) nfce.tipo_emissao (coluna SQLite)
     * 2) nfce.tpEmis
     * 3) dados_json.ide.tpEmis
     * 4) parse de <tpEmis>X</tpEmis> em xml_autorizado/xml_assinado/xml
     * 5) fallback Normal (1)
     *
     * Nunca usa status_fiscal para deduzir contingência.
     */
    getTpEmis(nfce) {
      if (!nfce) return 1;

      const tipoCol = nfce.tipo_emissao;
      if (tipoCol != null && String(tipoCol) !== "") {
        const n = Number(tipoCol);
        if (n === 1 || n === 9) return n;
      }
      const tpe = nfce.tpEmis;
      if (tpe != null && String(tpe) !== "") {
        const n = Number(tpe);
        if (n === 1 || n === 9) return n;
      }

      try {
        if (nfce.dados_json) {
          const d = typeof nfce.dados_json === "string" ? JSON.parse(nfce.dados_json) : nfce.dados_json;
          const fromJson = d?.ide?.tpEmis ?? d?.tpEmis;
          if (fromJson != null) {
            const n = Number(fromJson);
            if (n === 1 || n === 9) return n;
          }
        }
      } catch (_) { /* ignora */ }

      const xml = nfce.xml_autorizado || nfce.xml_assinado || nfce.xml;
      if (xml && typeof xml === "string") {
        const m = xml.match(/<tpEmis>\s*(\d)\s*<\/tpEmis>/i);
        if (m) {
          const n = Number(m[1]);
          if (n === 1 || n === 9) return n;
        }
      }

      return 1;
    },

    labelFiscal(sf) {
      return STATUS_LABEL[sf] || (sf || "—");
    },

    numeroVendaExibicao(nfce) {
      if (!nfce) return "—";
      if (this.statusEfetivo(nfce) === "pre_venda") {
        if (nfce.numero_venda != null && String(nfce.numero_venda).trim() !== "") {
          return nfce.numero_venda;
        }
        try {
          const payload = typeof nfce.payload_json === "string"
            ? JSON.parse(nfce.payload_json)
            : (nfce.payload_json || {});
          const dados = typeof payload.dados === "string"
            ? JSON.parse(payload.dados)
            : (payload.dados || {});
          return payload.numero_venda ?? dados.numero_venda ?? nfce.numero ?? nfce.numeroNota ?? "—";
        } catch (_) {
          return nfce.numero ?? nfce.numeroNota ?? "—";
        }
      }
      return nfce.numeroNota ?? nfce.numero ?? "—";
    },

    labelStatusVenda(nfce) {
      if (this.statusEfetivo(nfce) === "pre_venda" && Number(nfce.status) === 7) {
        return "Finalizada";
      }
      return this.labelFiscal(this.statusEfetivo(nfce));
    },

    badgeClassStatusVenda(nfce) {
      if (this.statusEfetivo(nfce) === "pre_venda" && Number(nfce.status) === 7) {
        return "vp-badge--success";
      }
      return this.badgeClassFiscal(this.statusEfetivo(nfce));
    },

    podeCancelarVenda(nfce) {
      const label = String(this.labelStatusVenda(nfce) || "")
        .trim()
        .toLocaleLowerCase("pt-BR");
      if (label === "finalizada") return true;
      if (label !== "autorizada") return false;

      // NFC-e autorizada exige os dados fiscais necessários para cancelar.
      return !!nfce.chave && (!!nfce.protocolo || !!nfce.xml_autorizado);
    },

    /**
     * Status efetivo da linha para exibição e ações.
     * Se as colunas status/statusTransmissao indicarem cancelamento (ex.: o back
     * cancelou a NFC-e), a venda é tratada como "cancelada" mesmo que o
     * status_fiscal ainda não tenha sido atualizado.
     */
    statusEfetivo(nfce) {
      if (!nfce) return "";
      const norm = (v) => String(v ?? "").trim().toLowerCase();
      const cancelado = ["cancelada", "cancelado"];
      if (cancelado.includes(norm(nfce.status)) || cancelado.includes(norm(nfce.statusTransmissao))) {
        return "cancelada";
      }
      return nfce.status_fiscal;
    },

    badgeClassFiscal(sf) {
      return STATUS_BADGE[sf] || "vp-badge--neutral";
    },

    _filtrosLimpos() {
      const f = this.filtros;
      const out = {};
      if (f.cliente)     out.cliente     = f.cliente;
      if (f.numero)      out.numero      = f.numero;
      if (f.dataInicio)  out.dataInicio  = f.dataInicio;
      if (f.dataFim)     out.dataFim     = f.dataFim;
      if (f.status)      out.status      = f.status;
      if (f.tipoEmissao) out.tipoEmissao = f.tipoEmissao;
      return out;
    },

    async aplicarFiltros() {
      this.paginacao.offset = 0;
      await this.recarregar();
    },

    async limparFiltros() {
      this.filtros = {
        cliente: "", numero: "", dataInicio: "", dataFim: "",
        status: "", tipoEmissao: "",
      };
      this.paginacao.offset = 0;
      await this.recarregar();
    },

    async recarregar() {
      this.carregando = true;
      this.erro       = null;
      try {
        // Reconcilia status com o back/web (ex.: NFC-e cancelada no painel web)
        // antes de listar, para a lista já refletir "Cancelada".
        try {
          await SyncPdvDesktopService.sincronizarStatusNfces();
        } catch (e) {
          console.warn("[VendasOffline] reconciliação de status NFC-e:", e?.message ?? e);
        }

        const filtros = this._filtrosLimpos();
        // eslint-disable-next-line no-console
        console.log("[VENDAS_PDV_FILTRO]", filtros);
        const lista = await NfceLocalRepository.listarVendas({
          ...filtros,
          limit:  this.paginacao.limit,
          offset: 0,
        });
        this.vendas = lista || [];
        // eslint-disable-next-line no-console
        console.log("[VENDAS_PDV_RESULTADOS]", this.vendas.length);
        this.paginacao.offset = this.vendas.length;
        this.podeCarregarMais = this.vendas.length === this.paginacao.limit;
      } catch (e) {
        this.erro = `Erro ao carregar vendas: ${e.message || e}`;
      } finally {
        this.carregando = false;
      }
    },

    async carregarMais() {
      this.carregandoMais = true;
      try {
        const lista = await NfceLocalRepository.listarVendas({
          ...this._filtrosLimpos(),
          limit:  this.paginacao.limit,
          offset: this.paginacao.offset,
        });
        const itens = lista || [];
        this.vendas = this.vendas.concat(itens);
        this.paginacao.offset += itens.length;
        this.podeCarregarMais = itens.length === this.paginacao.limit;
      } catch (e) {
        this.erro = `Erro ao carregar mais vendas: ${e.message || e}`;
      } finally {
        this.carregandoMais = false;
      }
    },

    toggleMenu(uuid) {
      this.menuAberto = this.menuAberto === uuid ? null : uuid;
    },

    closeMenu() {
      this.menuAberto = null;
    },

    // ── Ações disponíveis por status_fiscal ────────────────────────────────
    acoesDisponiveis(nfce) {
      const status = this.statusEfetivo(nfce);
      const base = [
        { key: "detalhes", label: "Visualizar detalhes", icone: "fas fa-info-circle" },
      ];
      const acoes = [...base];

      if (status === "autorizada") {
        acoes.push(
          { key: "reimprimir",          label: "Reimprimir DANFE",    icone: "fas fa-print" },
          { key: "ver_xml_autorizado", label: "Ver XML autorizado",  icone: "fas fa-code" },
          { key: "copiar_chave",       label: "Copiar chave",        icone: "fas fa-copy" },
          { key: "cancelar_fiscal",   label: "Cancelar NFC-e",      icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      } else if (status === "pre_venda") {
        acoes.push(
          { key: "imprimir_pre_venda", label: "Imprimir comprovante",      icone: "fas fa-print" },
          { key: "converter_nfce",   label: "Transformar em NFC-e",    icone: "fas fa-file-invoice" },
          { key: "cancelar",         label: "Cancelar pré-venda",      icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      } else if (status === "pendente_transmissao_backend" || status === "pendente_transmissao") {
        acoes.push(
          { key: "imprimir_contingencia", label: "Imprimir contingência", icone: "fas fa-print" },
          { key: "ver_xml_assinado",      label: "Ver XML assinado",      icone: "fas fa-code" },
          { key: "transmitir",            label: "Transmitir agora",      icone: "fas fa-paper-plane" },
          { key: "copiar_chave",          label: "Copiar chave",          icone: "fas fa-copy" },
          { key: "cancelar",              label: "Cancelar venda",        icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      } else if (status === "xml_assinado") {
        acoes.push(
          { key: "ver_xml_assinado", label: "Ver XML assinado", icone: "fas fa-code" },
          { key: "transmitir",       label: "Transmitir agora", icone: "fas fa-paper-plane" },
          { key: "copiar_chave",     label: "Copiar chave",     icone: "fas fa-copy" },
          { key: "cancelar",         label: "Cancelar venda",   icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      } else if (status === "rejeitada") {
        acoes.push(
          { key: "ver_erro",         label: "Ver erro",                icone: "fas fa-exclamation-triangle" },
          { key: "ver_xml_assinado", label: "Ver XML assinado",        icone: "fas fa-code" },
          { key: "corrigir",         label: "Corrigir fiscal",         icone: "fas fa-tools" },
          { key: "regerar",          label: "Gerar/assinar novamente", icone: "fas fa-redo" },
          { key: "transmitir",       label: "Transmitir novamente",    icone: "fas fa-paper-plane" },
          { key: "cancelar",         label: "Cancelar venda",          icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      } else if (status === "erro_transmissao" || status === "erro") {
        acoes.push(
          { key: "transmitir",       label: "Transmitir novamente", icone: "fas fa-paper-plane" },
          { key: "ver_erro",         label: "Ver erro técnico",     icone: "fas fa-exclamation-triangle" },
          { key: "ver_xml_assinado", label: "Ver XML assinado",     icone: "fas fa-code" },
          { key: "cancelar",         label: "Cancelar venda",       icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      } else if (status === "cancelada") {
        acoes.push(
          { key: "ver_xml_autorizado",   label: "Ver XML autorizado",     icone: "fas fa-code" },
          { key: "ver_xml_cancelamento", label: "Ver XML cancelamento",   icone: "fas fa-file-code" },
          { key: "copiar_chave",         label: "Copiar chave",           icone: "fas fa-copy" },
          { key: "reimprimir",           label: "Reimprimir comprovante", icone: "fas fa-print" },
        );
      } else {
        // pendente_xml, xml_gerado e quaisquer outros
        acoes.push(
          { key: "copiar_chave", label: "Copiar chave",    icone: "fas fa-copy" },
          { key: "cancelar",     label: "Cancelar venda",  icone: "fas fa-ban", classe: "vp-dropdown__item--danger" },
        );
      }

      const podeCancelar = this.podeCancelarVenda(nfce);
      return acoes.map((acao) => {
        if (acao.key !== "cancelar" && acao.key !== "cancelar_fiscal") return acao;
        return {
          ...acao,
          disabled: !podeCancelar,
          title: podeCancelar
            ? ""
            : "O cancelamento está disponível somente para vendas Autorizadas ou Finalizadas.",
        };
      });
    },

    async executarAcao(acao, nfce) {
      if (!acao || acao.disabled === true) return;
      const key = typeof acao === "string" ? acao : acao.key;
      this.closeMenu();
      this.erro    = null;
      this.sucesso = null;

      switch (key) {
        case "detalhes":               return this.abrirDetalhes(nfce);
        case "ver_xml_autorizado":     return this.abrirXmlAutorizado(nfce);
        case "ver_xml_assinado":       return this.abrirXmlAssinado(nfce);
        case "ver_xml_cancelamento":   return this.abrirXmlCancelamento(nfce);
        case "ver_erro":               return this.abrirDetalhes(nfce); // detalhe já mostra motivo
        case "copiar_chave":           return this.copiarChave(nfce);
        case "reimprimir":             return this.imprimir(nfce);
        case "imprimir_contingencia":  return this.imprimir(nfce);
        case "imprimir_pre_venda":     return this.imprimirPreVenda(nfce);
        case "transmitir":             return this.transmitirAgora(nfce);
        case "converter_nfce":         return this.converterPreVenda(nfce);
        case "corrigir":               return this.abrirCorrigirFiscal(nfce);
        case "regerar":                return this.regerarXml(nfce);
        case "cancelar_fiscal":        return this.abrirCancelarFiscal(nfce);
        case "cancelar":               return this.cancelarVenda(nfce);
        default:                       return;
      }
    },

    // ── Ações ──────────────────────────────────────────────────────────────
    abrirDetalhes(nfce) {
      this.modalDetalhe = { visivel: true, nfce };
    },

    abrirCorrigirFiscal(nfce) {
      this.modalCorrigir = { visivel: true, nfce };
    },

    async abrirXmlAutorizado(nfce) {
      let atualizado = nfce;
      try {
        const api  = getElectronAPI();
        const full = await api.nfce.getByUuid(nfce.uuid_venda_terminal);
        if (full) atualizado = full;
      } catch (_) { /* segue com o objeto da listagem */ }

      this.modalXml = {
        visivel:       true,
        nfce:          atualizado,
        tipo:          "autorizado",
        titulo:        "XML autorizado",
        conteudo:      atualizado.xml_autorizado || "",
        mensagemVazio: "XML autorizado ainda não disponível para esta NFC-e.",
      };
      this.xmlCopiado    = false;
      this.mensagemToast = null;
    },

    async abrirXmlAssinado(nfce) {
      let atualizado = nfce;
      try {
        const api  = getElectronAPI();
        const full = await api.nfce.getByUuid(nfce.uuid_venda_terminal);
        if (full) atualizado = full;
      } catch (_) { /* segue com o objeto da listagem */ }

      this.modalXml = {
        visivel:       true,
        nfce:          atualizado,
        tipo:          "assinado",
        titulo:        "XML assinado",
        conteudo:      atualizado.xml_assinado || atualizado.xml || "",
        mensagemVazio: "XML assinado ainda não disponível para esta NFC-e.",
      };
      this.xmlCopiado    = false;
      this.mensagemToast = null;
    },

    async abrirXmlCancelamento(nfce) {
      let atualizado = nfce;
      try {
        const api  = getElectronAPI();
        const full = await api.nfce.getByUuid(nfce.uuid_venda_terminal);
        if (full) atualizado = full;
      } catch (_) { /* segue com o objeto da listagem */ }

      this.modalXml = {
        visivel:       true,
        nfce:          atualizado,
        tipo:          "cancelamento",
        titulo:        "XML de cancelamento",
        conteudo:      atualizado.xml_cancelamento || "",
        mensagemVazio: "XML de cancelamento ainda não disponível para esta NFC-e.",
      };
      this.xmlCopiado    = false;
      this.mensagemToast = null;
    },

    fecharModalXml() {
      this.modalXml.visivel = false;
      this.xmlCopiado       = false;
      this.mensagemToast    = null;
    },

    copiarXmlAtivo() {
      const conteudo = this.modalXml.conteudo;
      if (!conteudo) return;
      navigator.clipboard.writeText(conteudo).then(() => {
        this.xmlCopiado    = true;
        if (this.modalXml.tipo === "autorizado") {
          this.mensagemToast = "XML autorizado copiado.";
        } else if (this.modalXml.tipo === "cancelamento") {
          this.mensagemToast = "XML de cancelamento copiado.";
        } else {
          this.mensagemToast = "XML assinado copiado.";
        }
        setTimeout(() => { this.xmlCopiado = false; this.mensagemToast = null; }, 2200);
      }).catch(() => {
        this.erro = "Erro ao copiar XML para a área de transferência.";
      });
    },

    copiarChave(nfce) {
      if (!nfce.chave) return;
      navigator.clipboard.writeText(nfce.chave).then(() => {
        this.sucesso = "Chave copiada para a área de transferência.";
        setTimeout(() => { this.sucesso = null; }, 2000);
      }).catch(() => {
        this.erro = "Erro ao copiar chave.";
      });
    },

    // ── Backup / Exportar XMLs ─────────────────────────────────────────────
    abrirBackupXml() {
      this.modalBackup = {
        visivel:    true,
        dataInicio: this.filtros.dataInicio || "",
        dataFim:    this.filtros.dataFim || "",
        pasta:      "",
        resumo:     null,
      };
      this.backupErro = null;
    },

    fecharBackupXml() {
      if (this.exportandoXml) return;
      this.modalBackup.visivel = false;
    },

    async selecionarPastaBackup() {
      this.backupErro = null;
      try {
        const api = getElectronAPI();
        const res = await api.backup.selecionarPasta();
        if (res && !res.canceled && res.pasta) {
          this.modalBackup.pasta = res.pasta;
        }
      } catch (e) {
        this.backupErro = `Erro ao abrir seletor de pasta: ${e.message || e}`;
      }
    },

    async exportarXmls() {
      this.backupErro = null;
      this.modalBackup.resumo = null;
      if (!this.modalBackup.pasta) {
        this.backupErro = "Escolha uma pasta de destino.";
        return;
      }
      this.exportandoXml = true;
      try {
        const api = getElectronAPI();
        const resumo = await api.backup.exportarXmls({
          destDir:    this.modalBackup.pasta,
          dataInicio: this.modalBackup.dataInicio || null,
          dataFim:    this.modalBackup.dataFim || null,
        });
        this.modalBackup.resumo = resumo;
        this.sucesso = `Backup concluído: ${resumo.exportados} XML(s) exportado(s).`;
        setTimeout(() => { this.sucesso = null; }, 4000);
      } catch (e) {
        this.backupErro = `Erro ao exportar XMLs: ${e.message || e}`;
      } finally {
        this.exportandoXml = false;
      }
    },

    async imprimir(nfce) {
      try {
        const api = getElectronAPI();
        if (api?.printer?.imprimirDanfeNfce) {
          await api.printer.imprimirDanfeNfce({ uuid_venda_terminal: nfce.uuid_venda_terminal });
        } else {
          await api.nfce.imprimir(nfce.uuid_venda_terminal);
        }
        this.sucesso = "Comando de impressão enviado.";
      } catch (e) {
        this.erro = `Erro ao imprimir: ${e.message || e}`;
      }
    },

    async imprimirPreVenda(nfce) {
      try {
        const api = getElectronAPI();
        if (!api?.printer?.imprimirComprovantePreVenda) {
          throw new Error("Impressão de comprovante de pré-venda não disponível.");
        }
        await api.printer.imprimirComprovantePreVenda({
          uuid_venda_terminal: nfce.uuid_venda_terminal,
        });
        this.sucesso = "Comprovante de pré-venda enviado para impressão.";
      } catch (e) {
        this.erro = `Erro ao imprimir comprovante: ${e.message || e}`;
      }
    },

    async transmitirAgora(nfce) {
      // Venda cancelada (local ou no back/web): não permite transmitir.
      if (this.statusEfetivo(nfce) === "cancelada") {
        this.erro = "Esta NFC-e está cancelada e não pode ser transmitida.";
        return;
      }
      try {
        // Uma NFC-e rejeitada mantém o XML assinado que foi enviado. Antes da
        // nova tentativa, regenera e assina para aplicar correções fiscais
        // (por exemplo, atualização da URL estadual do QR Code), preservando
        // chave, número e série já reservados para a venda.
        if (this.statusEfetivo(nfce) === "rejeitada") {
          const api = getElectronAPI();
          await api.nfce.prepararParaRegerarXml(nfce.uuid_venda_terminal);
          await api.nfce.gerarXml(nfce.uuid_venda_terminal);
          await api.nfce.assinarXml(nfce.uuid_venda_terminal);
        }

        const resultado = await SyncPdvDesktopService.transmitirNfce(nfce.uuid_venda_terminal);
        const status    = resultado?.status;
        if (status === "autorizada" || status === "ja_autorizada") {
          this.sucesso = `NFC-e autorizada! Protocolo: ${resultado.protocolo || "—"}`;
        } else if (status === "rejeitada") {
          this.erro = `Rejeitada pela SEFAZ: ${resultado.motivo || resultado.codigo || "sem motivo"}`;
        } else {
          this.erro = `Transmissão com status: ${status || "desconhecido"}`;
        }
        await this._atualizarLinha(nfce.uuid_venda_terminal);
      } catch (e) {
        this.erro = `Erro ao transmitir: ${e.message || e}`;
      }
    },

    async converterPreVenda(nfce) {
      if (!this.online) {
        this.erro = "É necessário estar online para transformar a pré-venda em NFC-e.";
        return;
      }
      const ok = window.confirm(
        `Transformar a pré-venda nº ${this.numeroVendaExibicao(nfce)} em NFC-e e transmiti-la à SEFAZ?`
      );
      if (!ok) return;

      try {
        const resultado = await NfcePdvDesktopService.converterPreVendaEmNfce(
          nfce.uuid_venda_terminal
        );
        if (resultado?.status === "autorizada" || resultado?.status === "ja_autorizada") {
          this.sucesso = `Pré-venda transformada em NFC-e. Protocolo: ${resultado.protocolo || "—"}`;
        } else if (resultado?.status === "rejeitada") {
          this.erro = `NFC-e rejeitada pela SEFAZ: ${resultado.motivo || resultado.codigo || "sem motivo"}`;
        } else {
          const detalhe =
            resultado?.motivo || resultado?.erro || resultado?.message;
          this.erro = detalhe
            ? `Não foi possível transformar a pré-venda em NFC-e: ${detalhe}`
            : `Conversão retornou o status: ${
                resultado?.status || "desconhecido"
              }`;
        }
        await this._atualizarLinha(nfce.uuid_venda_terminal);
      } catch (e) {
        this.erro = `Erro ao transformar pré-venda em NFC-e: ${e.message || e}`;
      }
    },

    async regerarXml(nfce) {
      // Venda cancelada (local ou no back/web): não permite regerar XML.
      if (this.statusEfetivo(nfce) === "cancelada") {
        this.erro = "Esta NFC-e está cancelada e não pode ter o XML regerado.";
        return;
      }
      try {
        const api = getElectronAPI();
        await api.nfce.prepararParaRegerarXml(nfce.uuid_venda_terminal);
        const result = await api.nfce.gerarXml(nfce.uuid_venda_terminal);
        try {
          await api.nfce.assinarXml(nfce.uuid_venda_terminal);
          this.sucesso = `XML gerado e assinado. Chave: ${result.chave}`;
        } catch (errAssin) {
          this.sucesso = `XML gerado. Falha ao assinar: ${errAssin.message || errAssin}`;
        }
        await this._atualizarLinha(nfce.uuid_venda_terminal);
      } catch (e) {
        this.erro = `Erro ao gerar XML novamente: ${e.message || e}`;
      }
    },

    async cancelarVenda(nfce) {
      const status = this.statusEfetivo(nfce);
      // Venda já cancelada (local ou no back/web): bloqueia nova ação de cancelamento.
      if (status === "cancelada") {
        this.erro = "Esta NFC-e já está cancelada.";
        return;
      }
      if (!this.podeCancelarVenda(nfce)) {
        this.erro = "O cancelamento está disponível somente para vendas Autorizadas ou Finalizadas.";
        return;
      }
      if (status === "autorizada") {
        // Cancelamento fiscal de NFC-e autorizada usa modal próprio
        return this.abrirCancelarFiscal(nfce);
      }

      const ok = window.confirm(
        status === "pre_venda"
          ? "Deseja cancelar esta pré-venda? O estoque e os lançamentos financeiros serão estornados."
          : "Deseja cancelar esta NFC-e? Esta ação deve ser usada somente quando a venda realmente precisar ser cancelada."
      );
      if (!ok) return;

      try {
        if (status === "pre_venda") {
          const resultado = await SyncPdvDesktopService.cancelarPreVenda(
            nfce.uuid_venda_terminal,
            "Pré-venda cancelada pelo operador no PDV Desktop."
          );
          this.sucesso = resultado.sincronizada
            ? "Pré-venda cancelada e sincronizada com a nuvem."
            : "Pré-venda cancelada. O estorno na nuvem será sincronizado quando houver conexão.";
          await this.recarregar();
          window.dispatchEvent(new CustomEvent("pdv-dashboard-refresh"));
          return;
        }

        await NfceLocalRepository.cancelarLocal(
          nfce.uuid_venda_terminal,
          "Cancelada localmente pelo operador (venda não autorizada)."
        );
        this.sucesso = "Venda cancelada localmente.";
        await this.recarregar();
        window.dispatchEvent(new CustomEvent("pdv-dashboard-refresh"));
      } catch (e) {
        this.erro = `Erro ao cancelar venda: ${e.message || e}`;
      }
    },

    abrirCancelarFiscal(nfce) {
      // Venda já cancelada (local ou no back/web): não reabre o cancelamento.
      if (this.statusEfetivo(nfce) === "cancelada") {
        this.erro = "Esta NFC-e já está cancelada.";
        return;
      }
      if (!this.podeCancelarVenda(nfce)) {
        this.erro = "O cancelamento está disponível somente para vendas Autorizadas ou Finalizadas.";
        return;
      }
      this.modalCancelar = { visivel: true, nfce };
    },

    async fecharModalCancelar() {
      // Substitui o estado inteiro para desmontar o modal imediatamente e
      // descartar a NFC-e anterior antes de consultar novamente a listagem.
      this.modalCancelar = { visivel: false, nfce: null };
      await this.recarregar();
    },

    async _onNfceCancelada(resultado) {
      this.sucesso = resultado && resultado.motivo
        ? `NFC-e cancelada na SEFAZ. ${resultado.motivo}`
        : "NFC-e cancelada com sucesso na SEFAZ.";

      await this.fecharModalCancelar();

      window.dispatchEvent(new CustomEvent("pdv-dashboard-refresh"));
    },

    async sincronizarPendentes() {
      if (!this.online) {
        this.erro = "Sincronização disponível somente com internet.";
        return;
      }
      this.sincronizandoPendentes = true;
      this.erro    = null;
      this.sucesso = null;
      try {
        const [resultado, preVendas, cancelamentos] = await Promise.all([
          SyncPdvDesktopService.sincronizarNfcesPendentes(),
          SyncPdvDesktopService.sincronizarPreVendasPendentes(),
          SyncPdvDesktopService.sincronizarCancelamentosPreVendaPendentes(),
        ]);
        const erros = Number(resultado.erros || 0)
          + Number(preVendas.erros || 0)
          + Number(cancelamentos.erros || 0);
        if (resultado.transmitidas > 0 || preVendas.enviadas > 0 || cancelamentos.enviadas > 0) {
          this.sucesso = `${resultado.transmitidas || 0} NFC-e autorizada(s), ${preVendas.enviadas || 0} pré-venda(s) sincronizada(s), ${cancelamentos.enviadas || 0} cancelamento(s) sincronizado(s), ${erros} erro(s).`;
        } else if (erros > 0) {
          this.erro = `${erros} registro(s) com erro de sincronização.`;
        } else {
          this.sucesso = "Nenhuma NFC-e pendente.";
        }
        await this.recarregar();
      } catch (e) {
        this.erro = `Erro ao sincronizar: ${e.message || e}`;
      } finally {
        this.sincronizandoPendentes = false;
      }
    },

    async _atualizarLinha(uuid) {
      try {
        const api  = getElectronAPI();
        const full = await api.nfce.getByUuid(uuid);
        if (!full) return;
        const idx = this.vendas.findIndex((v) => v.uuid_venda_terminal === uuid);
        if (idx !== -1) this.$set(this.vendas, idx, full);
      } catch (_) { /* ignora */ }
    },

    async _onCorrecaoAtualizada() {
      await this.recarregar();
      if (this.modalCorrigir.visivel && this.modalCorrigir.nfce) {
        const uuid = this.modalCorrigir.nfce.uuid_venda_terminal;
        try {
          const api = getElectronAPI();
          const atualizado = await api.nfce.getByUuid(uuid);
          if (atualizado) this.modalCorrigir.nfce = atualizado;
        } catch (_) { /* ignora */ }
      }
    },
  },
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────────── */
.vendas-pdv-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: none;
  margin: 0;
  padding: 8px;
  box-sizing: border-box;
  min-height: 0;
  background: #f5f7fa;
  overflow: hidden;
}

/* ── Menu superior de navegação (padrão NovaVenda) ──────────────────────── */
.vendas-pdv-menu {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  padding: 10px;
  margin-bottom: 12px;
  background: #f7fafc;
  border: 1px solid #f0f4f8;
  border-radius: 8px;
}

.vp-menu-btn {
  min-width: 0;
  padding: 10px 8px;
  border: none;
  border-radius: 7px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}

.vp-menu-btn > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vp-menu-btn--secondary {
  background: #edf2f7;
  color: #4a5568;
}

.vp-menu-btn--secondary:hover {
  background: #e2e8f0;
}

.vp-menu-btn--active,
.vp-menu-btn--active:disabled {
  background: var(--primary-color, #ee7361);
  color: #fff;
  cursor: default;
  opacity: 1;
}

.vp-menu-btn__atalho {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  padding: 1px 6px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.12);
  color: inherit;
  border-radius: 4px;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.page-header {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}
.page-header__title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: #212529;
}
.page-header__sub {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0.25rem 0 0 0;
}
.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

/* ── Buttons ─────────────────────────────────────────────────────────────── */
.vp-btn {
  padding: 0.5rem 1rem;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  display: inline-flex;
  align-items: center;
  transition: opacity .15s, background .15s;
  background: #fff;
  color: #495057;
}
.vp-btn:disabled { opacity: .55; cursor: not-allowed; }
.vp-btn--primary { background: var(--primary-color, #ee7361); color: #fff; border-color: var(--primary-color, #ee7361); }
.vp-btn--primary:hover:not(:disabled) { background: #e05a47; }
.vp-btn--outline { background: #fff; color: #495057; border-color: #ced4da; }
.vp-btn--outline:hover:not(:disabled) { background: #f1f3f5; }
.vp-btn--ghost   { background: transparent; color: #495057; border-color: transparent; }
.vp-btn--ghost:hover:not(:disabled) { background: #e9ecef; }
.vp-btn--sm      { padding: 0.35rem 0.75rem; font-size: 0.8rem; }

/* ── Filtros ─────────────────────────────────────────────────────────────── */
.filters-card {
  flex-shrink: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.05);
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
}
.filters-grid {
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 0.9fr 0.9fr 1.1fr 0.9fr auto;
  gap: 0.75rem;
  align-items: end;
}
.filter-field { display: flex; flex-direction: column; }
.filter-field label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.filter-field input, .filter-field select {
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  border: 1px solid #ced4da;
  font-size: 0.85rem;
  background: #fff;
}
.filter-field input:focus, .filter-field select:focus {
  outline: none;
  border-color: var(--primary-color, #ee7361);
  box-shadow: 0 0 0 2px rgba(238,115,97,.15);
}
.filter-actions {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

@media (max-width: 1200px) {
  .filters-grid { grid-template-columns: repeat(3, 1fr); }
  .filter-actions { grid-column: span 3; justify-content: flex-end; }
}

/* ── Tabela ──────────────────────────────────────────────────────────────── */
.sales-table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none;
  min-height: 360px;
  min-width: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,.06);
  overflow: visible;
}
.sales-table-wrapper {
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: auto;
  padding-bottom: 12rem;
  box-sizing: border-box;
}
.vp-loading {
  padding: 2rem;
  text-align: center;
  color: #6c757d;
}
.vp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.87rem;
}
.vp-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #6c757d;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  white-space: nowrap;
}
.vp-table th.text-right  { text-align: right; }
.vp-table th.text-center { text-align: center; }
.vp-table td {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
}
.vp-table tr:last-child td { border-bottom: none; }
.vp-table__actions {
  position: relative;
  width: 1%;
  min-width: 124px;
  white-space: nowrap;
  overflow: visible;
}
.vp-numero { font-weight: 700; }
.font-monospace { font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; }

.vp-chave {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 0.8rem;
  color: #495057;
}
.vp-icon-btn {
  background: transparent;
  border: none;
  padding: 0.15rem 0.35rem;
  color: #6c757d;
  cursor: pointer;
  border-radius: 4px;
}
.vp-icon-btn:hover { background: #f1f3f5; color: var(--primary-color, #ee7361); }

/* ── Badges ──────────────────────────────────────────────────────────────── */
.vp-badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.vp-badge--success { background: #d1e7dd; color: #0a3622; }
.vp-badge--warning { background: #fff3cd; color: #856404; }
.vp-badge--danger  { background: #f8d7da; color: #58151c; }
.vp-badge--info    { background: #cff4fc; color: #055160; }
.vp-badge--neutral { background: #e9ecef; color: #495057; }

/* ── Dropdown de ações ───────────────────────────────────────────────────── */
.vp-dropdown {
  position: absolute;
  right: 0.75rem;
  top: calc(100% + 4px);
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0,0,0,.12);
  min-width: 230px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 1000;
  padding: 0.35rem 0;
  display: flex;
  flex-direction: column;
}
.vp-dropdown__item {
  background: transparent;
  border: none;
  padding: 0.5rem 0.85rem;
  text-align: left;
  font-size: 0.85rem;
  color: #343a40;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
.vp-dropdown__item:hover { background: #f1f3f5; }
.vp-dropdown__item--danger { color: #b02a37; }
.vp-dropdown__item--danger:hover { background: #f8d7da; color: #842029; }
.vp-dropdown__item:disabled {
  color: #adb5bd;
  background: transparent;
  cursor: not-allowed;
  opacity: 0.65;
}
.vp-dropdown__item--danger:disabled:hover { background: transparent; color: #adb5bd; }

/* ── Load more ───────────────────────────────────────────────────────────── */
.vp-load-more {
  padding: 1rem;
  text-align: center;
  border-top: 1px solid #f1f3f5;
}

/* ── Modal XML ───────────────────────────────────────────────────────────── */
.vp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.vp-modal-xml {
  background: #fff;
  border-radius: 8px;
  width: 820px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.vp-modal-xml__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
}
.vp-modal-xml__title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}
.vp-modal-xml__empty {
  flex: 1;
  padding: 2rem 1.5rem;
  text-align: center;
  color: #6c757d;
  font-size: .9rem;
}
.vp-modal-xml__content {
  flex: 1;
  overflow: auto;
  padding: 1rem 1.25rem;
  font-size: 0.76rem;
  font-family: monospace;
  background: #f8f9fa;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}
.vp-modal-xml__footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.vp-modal-backup {
  background: #fff;
  border-radius: 8px;
  width: 520px;
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.vp-modal-backup__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
}
.vp-modal-backup__title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}
.vp-modal-backup__body {
  padding: 1rem 1.25rem;
  overflow: auto;
}
.vp-modal-backup__footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}
.vp-backup-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.vp-backup-pasta {
  display: flex;
  gap: 0.5rem;
}
.vp-backup-pasta input {
  flex: 1;
}
.vp-backup-resumo {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 0.85rem;
}
.vp-backup-resumo h5 {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}
.vp-backup-resumo ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.vp-backup-resumo li {
  padding: 2px 0;
}
.vp-backup-resumo__path code {
  word-break: break-all;
}
</style>
